import moment from "moment";

import { actions as fundcorpActions } from "reducers/fundcorp";
import { maskedAccountNumber } from "util/accounts";
import * as configUtils from "util/config";
import * as i18n from "util/i18n";

export const toSafeMoment = (value, fallback) => {
    if (!value) {
        return fallback;
    }
    const parsed = moment(value);
    return parsed.isValid() ? parsed : fallback;
};

export const prepareFundcorpOperationsEntry = (dispatch, { optionSelected } = {}) => {
    if (optionSelected) {
        dispatch(fundcorpActions.saveOptionSelected(optionSelected));
    }
    dispatch(fundcorpActions.listFundcorpPre("operations"));
};

const getRescueDeadlineLabel = (rescatePlazo) => {
    const formattedRescatePlazo = rescatePlazo.toString().toLowerCase();
    switch (formattedRescatePlazo) {
        case "0":
        case "inmediato":
            return i18n.get("fund.deadline.0");
        case "1":
            return i18n.get("fund.deadline.1");
        case "2":
            return i18n.get("fund.deadline.2");
        default:
            return `${rescatePlazo} dias`;
    }
};

export const getIbsAccount = (loggedUserAccounts, monetaryAccount) => {
    if (!loggedUserAccounts || loggedUserAccounts.length < 0 || !monetaryAccount) {
        return {};
    }
    const account = loggedUserAccounts.find((userAccount) => userAccount.cbu === monetaryAccount.cbu);
    return account;
};

export const getProductLabel = (ibsAccount) => {
    if (!ibsAccount || !ibsAccount?.number || !ibsAccount?.idProduct) {
        return i18n.get("fund.operation.subscribe.debitAccount.noAccount");
    }

    const { productAlias, number, productType, currency } = ibsAccount;
    return `${productAlias || ""} ${maskedAccountNumber(number)} ${productType} ${i18n.get(
        `currency.label.${currency}`,
    )}`;
};

export const isEnabledMvp = (mvp) => configUtils.getBoolean(`frontend.show.${mvp.toUpperCase()}.functionalities`);

export const fundStatusTagColor = (state) => {
    switch (state.toUpperCase()) {
        case "LIQUIDADA":
            return "#84EFD0";
        case "ANULADA":
            return "#FEB0AF";
        case "PENDIENTE DE LIQUIDACIÓN":
            return "#DDDDDD";
        case "RECHAZADA":
            return "#FEB0AF";
        case "NO REQUIERE AUTORIZACIÓN":
            return "#DDDDDD";
        default:
            return "#FEB0AF";
    }
};
export const isEnabledApi5 = () => configUtils.getBoolean("frontend.show.fundsAPI5.advisory.functionalities", false);
export const isEnabledFundPerformance = () =>
    configUtils.getBoolean("frontend.enable.performance.funds.functionalities", false) &&
    configUtils.getBoolean("frontend.fund.enableMVP.v9", false);

export const getPerformanceTooltip = () =>
    configUtils.get("frontend.performance.funds.text", i18n.get("fundcorp.performance.tooltip.default"));

/**
 * Pathname del flujo de suscripción (no usar como "pantalla anterior" al volver desde otra URL del mismo flujo).
 *
 * @param {string} pathname
 * @returns {boolean}
 */
export const isFundcorpSubscribeFlowPathname = (pathname) =>
    typeof pathname === "string" &&
    (pathname === "/fundcorpOperations/subscribe" ||
        pathname.startsWith("/fundcorpOperations/subscribe/") ||
        pathname.startsWith("/subscribe"));

/**
 * Pathname del flujo de rescate (misma regla que suscripción).
 *
 * @param {string} pathname
 * @returns {boolean}
 */
export const isFundcorpRescueFlowPathname = (pathname) =>
    typeof pathname === "string" &&
    (pathname === "/fundcorpOperations/rescue" ||
        pathname.startsWith("/fundcorpOperations/rescue/") ||
        pathname.startsWith("/rescue"));

/**
 * Volver desde suscripción/rescate: `state.return` si existe; si no, última ruta en `status.lastHref`
 * si no es otra pantalla del mismo flujo; por defecto `/fundcorpOperations`.
 *
 * @param {{ returnFromState?: string, lastHrefPathname?: string, currentPathname: string }} params
 * @returns {string}
 */
export const resolveFundcorpFlowGoBackPath = ({ returnFromState, lastHrefPathname, currentPathname }) => {
    if (returnFromState && returnFromState !== "/") {
        return returnFromState;
    }
    const last = typeof lastHrefPathname === "string" ? lastHrefPathname : "";
    if (
        last &&
        last !== "/" &&
        last !== currentPathname &&
        !isFundcorpSubscribeFlowPathname(last) &&
        !isFundcorpRescueFlowPathname(last)
    ) {
        return last;
    }
    return "/fundcorpOperations";
};

/**
 * Pathname de la pantalla de información / resúmenes de fondos (`/fundInformation`).
 *
 * @param {string} pathname
 * @returns {boolean}
 */
export const isFundInformationPathname = (pathname) =>
    typeof pathname === "string" && (pathname === "/fundInformation" || pathname.startsWith("/fundInformation/"));

/**
 * Volver desde `/fundInformation`: prioriza `state.return`, luego `status.lastHref` si no es la misma ruta
 * del módulo; por defecto `embeddedFallbackHome` (shell v3: típicamente `/v3/dashboard`).
 *
 * @param {{
 *   returnFromState?: string,
 *   lastHrefPathname?: string,
 *   currentPathname: string,
 *   embeddedFallbackHome?: string,
 * }} params
 * @returns {string}
 */
export const resolveFundInformationGoBackPath = ({
    returnFromState,
    lastHrefPathname,
    currentPathname,
    embeddedFallbackHome = "/v3/dashboard",
}) => {
    if (returnFromState && returnFromState !== "/") {
        return returnFromState;
    }
    const last = typeof lastHrefPathname === "string" ? lastHrefPathname : "";
    if (last && last !== "/" && last !== currentPathname && !isFundInformationPathname(last)) {
        return last;
    }
    return embeddedFallbackHome;
};

/**
 * Pathname del módulo Metrocorp (lista principal y subrutas).
 *
 * @param {string} pathname
 * @returns {boolean}
 */
export const isMetrocorpFlowPathname = (pathname) =>
    typeof pathname === "string" && (pathname === "/metrocorp" || pathname.startsWith("/metrocorp/"));

/**
 * Volver desde Metrocorp: `state.return` si existe; si no, `status.lastHref` si no es otra URL del mismo
 * flujo; por defecto `/metrocorp`.
 *
 * @param {{ returnFromState?: string, lastHrefPathname?: string, currentPathname: string }} params
 * @returns {string}
 */
export const resolveMetrocorpFlowGoBackPath = ({ returnFromState, lastHrefPathname, currentPathname }) => {
    if (returnFromState && returnFromState !== "/") {
        return returnFromState;
    }
    const last = typeof lastHrefPathname === "string" ? lastHrefPathname : "";
    if (last && last !== "/" && last !== currentPathname && !isMetrocorpFlowPathname(last)) {
        return last;
    }
    return "/metrocorp";
};

/**
 * Returns true when the user has at least one fund position in any share account.
 */
export const hasFundPositions = (positionsByShareAccount) =>
    (positionsByShareAccount || []).some((group) => Array.isArray(group.positionList) && group.positionList.length > 0);

/**
 * Resolves the default share account id for fund performance selectors.
 * Preload from URL wins; then favorite (if flag on); then sole account; else null (user must pick).
 */
export const getDefaultShareAccountId = (shareAccounts, preloadShareAccountId) => {
    if (preloadShareAccountId !== undefined && preloadShareAccountId !== null && preloadShareAccountId !== "") {
        return String(preloadShareAccountId);
    }
    const accounts = shareAccounts || [];
    if (accounts.length === 0) {
        return null;
    }
    if (accounts.length === 1) {
        return String(accounts[0].id);
    }
    if (configUtils.getBoolean("frontend.favoriteAccount.Funds.functionalities", false)) {
        const favorite = accounts.find((account) => account.isFavorite);
        if (favorite) {
            return String(favorite.id);
        }
    }
    return null;
};

export default getRescueDeadlineLabel;

// Se valida si la suscripcion tiene cancelacion inmediata para mostrar mensaje correspondiente
export const validateMessageCancellation = (numberFund, fundName) => {
    let messageCancellation;

    if (numberFund && fundName) {
        const cancelableLlist = configUtils.getArrayInteger("frontend.investment.funds.cancelable.list");
        const isCanceleable = cancelableLlist?.some((fund) => fund === numberFund);
        if (isCanceleable) {
            messageCancellation = configUtils
                .get("frontend.investment.funds.cancellation.message")
                ?.replace("#{NOMBRE_FONDO}", fundName);
        }
    }
    return messageCancellation;
};

export const hasPendingFundRequest = (fondoNumero, cuotapartistaNumero, pendingFundRequests) => {
    if (
        !pendingFundRequests ||
        !Array.isArray(pendingFundRequests) ||
        fondoNumero === undefined ||
        fondoNumero === null
    ) {
        return false;
    }

    const allowedTypesConfig = configUtils.get("frontend.balance.adjustment.funds.type", "Suscripción");
    const allowedTypes = new Set(allowedTypesConfig.split("|").map((type) => type.trim()));

    const fondoNumeroNumber = Number(fondoNumero);
    return pendingFundRequests.some(
        (request) =>
            request.cuotapartistaNumero === cuotapartistaNumero &&
            allowedTypes.has(request.tipoDescripcion) &&
            (request.fondoNumero === fondoNumero || Number(request.fondoNumero) === fondoNumeroNumber),
    );
};
